import uniqBy from 'lodash/uniqBy';
import { takeEvery, call, put } from 'redux-saga/effects';
import actionTypes from '../actionTypes';
import {
	createRuleSuccess,
	createRuleFailure,
	deleteRuleSuccess,
	deleteRuleFailure,
	getDataSuccessAction,
	getDataFailureAction,
	getSearchItemResultsSuccess,
	getSearchItemResultsFailure,
	getBulkItemSearchResultsSuccess,
	getBulkItemSearchResultsFailure,
	putNotificationsReadSuccess,
	putNotificationsReadFailure,
	getWatchListSuccess,
	getWatchListFailure,
	getSubRuleNotificationSuccess,
	getSubRuleNotificationFailure,
} from '../actions/createRuleActions';
import APIENDPOINTS from '../../constants/apiconstants';
import { getData } from '../service/getDataService';
import { deleteData, putData, saveData } from '../service/saveDataService';
import { customRulePollDelay } from '../../constants/constant';

export function* createRuleSaga(action) {
	const { portalUserContext, request, type, edit } = action.payload;
	const isPreview = type === 'preview';
	const apiUrl = isPreview
		? `${APIENDPOINTS.CUSTOM_RULE_URL}/v2/watchlist/preview`
		: `${APIENDPOINTS.CUSTOM_RULE_URL}/v2/watchlist`;
	try {
		const addToWatchList = yield call(
			saveData,
			request,
			apiUrl,
			portalUserContext.result
		);

		if (!addToWatchList?.ok) {
			throw new Error(addToWatchList?.ok);
		}

		const payload = {
			previewMessage: addToWatchList?.json?.msg,
			lastCreatedRuleName: !isPreview ? request?.name : '',
		};

		if (edit) {
			payload['lastCreatedRuleName'] = '';
		}

		yield put(createRuleSuccess({ ...payload }));
	} catch (error) {
		yield put(createRuleFailure(error));
	}
}

export function* deleteRuleSaga(request) {
	const { portalUserContext, id } = request.payload;
	try {
		const response = yield call(
			deleteData,
			`${APIENDPOINTS.CUSTOM_RULE_URL}/v2/watchlist/${id}`,
			portalUserContext.result
		);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		yield put(deleteRuleSuccess(response?.json));
	} catch (error) {
		yield put(deleteRuleFailure(error));
	}
}

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export function* pollSaga(action) {
	const { portalUserContext } = action.payload;
	while (true) {
		try {
			const response = yield call(
				getData,
				`${APIENDPOINTS.CUSTOM_RULE_URL}/custom/alerts`,
				portalUserContext.result
			);

			if (response?.statusCode >= 400) {
				throw new Error(response?.statusCode);
			}
			yield put(getDataSuccessAction(response?.json));
		} catch (err) {
			yield put(getDataFailureAction(err));
		}
		yield delay(customRulePollDelay);
	}
}

export function* readNotifications(action) {
	const { portalUserContext } = action.payload;
	try {
		yield call(
			putData,
			`${APIENDPOINTS.CUSTOM_RULE_URL}/custom/alerts/read`,
			portalUserContext.result
		);

		yield put(putNotificationsReadSuccess());
	} catch (err) {
		yield put(putNotificationsReadFailure());
	}
}

const idAndNameKeyMap = {
	store: { id: 'storeId', name: 'storeName' },
	item: { id: 'itemId', name: 'itemName' },
	category: { id: 'catId', name: 'catName' },
	department: { id: 'deptId', name: 'deptName' },
	subcategory: { id: 'subCatId', name: 'subCatName' },
	upc: { id: 'wmUpcNbr', name: 'upcDesc' },
	brand: { id: 'brandId', name: 'brandName' },
};

const channels = {
	store: 'STORE',
	department: 'DEPARTMENT',
	category: 'CATEGORY',
	subcategory: 'SUB_CATEGORY',
	item: 'ITEM',
	upc: 'UPC',
	brand: 'BRAND',
};

export function* getSearchItemResults(action) {
	const { type, inputQuery, portalUserContext } = action.payload;

	const TYPE = type.toLocaleLowerCase();
	try {
		const path = channels[TYPE] || 'STORE';

		const { id } = idAndNameKeyMap[TYPE];

		let response;

		if (path === 'BRAND') {
			response = yield call(
				getData,

				`${APIENDPOINTS.SEARCH_URL}/brandName/${inputQuery}?customrule=true`,
				portalUserContext.result
			);
		} else {
			response = yield call(
				getData,
				`${APIENDPOINTS.SEARCH_URL}/by/${path}?search=${inputQuery}&customrule=true`,
				portalUserContext.result
			);
		}

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		const { result = [], dimension } = response?.json?.result || {};

		yield put(
			getSearchItemResultsSuccess({
				dimension,
				[TYPE]: path === 'BRAND' ? result : uniqBy(result, id),
			})
		);
	} catch (error) {
		yield put(getSearchItemResultsFailure({ [TYPE]: [], errorCode: error }));
	}
}

export function* getBulkItemSearchResults(action) {
	const { type, inputQuery, portalUserContext } = action.payload;
	const TYPE = type.toLocaleLowerCase();
	try {
		const { id, name } = idAndNameKeyMap[TYPE];
		const reqBody = {
			channel: 'OMNI',
			dim: channels[TYPE] || 'STORE',
			validate: inputQuery.split(',') || [],
		};

		const response = yield call(
			saveData,
			reqBody,
			`${APIENDPOINTS.SUMMARY_URL}/V2/validate`,
			portalUserContext.result
		);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		const result = [];
		const { valid, invalid } = response?.json?.result || {};
		// Format data
		if (Array.isArray(valid)) {
			valid.forEach((e) => {
				result.push({
					[id]: e?.id,
					[name]: e?.name,
					isValid: true,
					checked: false,
				});
			});
		}
		if (Array.isArray(invalid)) {
			invalid.forEach((e) => {
				result.push({
					[id]: e,
					[name]: '',
					isValid: false,
					checked: false,
				});
			});
		}

		yield put(
			getBulkItemSearchResultsSuccess({
				[TYPE]: result,
			})
		);
	} catch (error) {
		yield put(
			getBulkItemSearchResultsFailure({ [TYPE]: [], errorCode: error })
		);
	}
}

export function* getWatchListSaga(action) {
	try {
		const { portalUserContext } = action.payload;

		const response = yield call(
			getData,
			`${APIENDPOINTS.CUSTOM_RULE_URL}/v2/watchlist`,
			portalUserContext.result
		);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		yield put(getWatchListSuccess(response?.json || []));
	} catch (error) {
		yield put(getWatchListFailure(error));
	}
}

export function* getSubRuleNotifications(action) {
	const { portalUserContext, parentId } = action.payload;
	const apiUrl = `${APIENDPOINTS.CUSTOM_RULE_URL}/custom/alerts?parentId=${parentId}`;
	try {
		const response = yield call(getData, apiUrl, portalUserContext.result);
		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		yield put(getSubRuleNotificationSuccess(response?.json));
	} catch (error) {
		yield put(getSubRuleNotificationFailure(error));
	}
}

export function* createRuleSagaWatcher() {
	yield takeEvery(actionTypes.GET_CREATE_RULE_DATA, createRuleSaga);
	yield takeEvery(actionTypes.POLL_START, pollSaga);
	yield takeEvery(actionTypes.GET_SEARCH_ITEM_RESULTS, getSearchItemResults);
	yield takeEvery(
		actionTypes.GET_BULK_ITEM_SEARCH_RESULTS,
		getBulkItemSearchResults
	);
	yield takeEvery(actionTypes.PUT_NOTIFICATIONS_READ, readNotifications);
	yield takeEvery(actionTypes.GET_WATCH_LIST, getWatchListSaga);
	yield takeEvery(actionTypes.DELETE_RULE, deleteRuleSaga);
	yield takeEvery(
		actionTypes.GET_SUB_RULE_NOTIFICATION,
		getSubRuleNotifications
	);
}
